import { useEffect, useState } from "react";
import { Plus, X, Trash2, Shield } from "lucide-react";
import { cockpit, useCockpitAuth } from "@/lib/cockpitAuth";
import { Field, Select } from "./CockpitDossiers";

const ROLES = [
    { v: "admin", l: "Administrateur" },
    { v: "super_admin", l: "Super-administrateur" },
];

export default function CockpitUsers() {
    const { user } = useCockpitAuth();
    const [items, setItems] = useState([]);
    const [creating, setCreating] = useState(false);

    const load = () => cockpit.get("/users").then(({ data }) => setItems(data));

    useEffect(() => {
        load();
    }, []);

    const setActive = async (email, active) => {
        await cockpit.put(`/users/${encodeURIComponent(email)}`, { active });
        load();
    };
    const setRole = async (email, role) => {
        await cockpit.put(`/users/${encodeURIComponent(email)}`, { role });
        load();
    };
    const remove = async (email) => {
        if (!window.confirm(`Supprimer définitivement le compte ${email} ?`)) return;
        await cockpit.delete(`/users/${encodeURIComponent(email)}`);
        load();
    };

    return (
        <div data-testid="cockpit-users" className="px-5 sm:px-8 lg:px-16 py-10 lg:py-14">
            <div className="flex items-end justify-between mb-12 gap-6">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-4">
                        Cercle restreint · Comptes back-office
                    </div>
                    <h1
                        className="font-serif text-[28px] sm:text-[34px] md:text-[40px] lg:text-[48px] leading-[1.08] text-[#f4ecd8]"
                        style={{ fontWeight: 300 }}
                    >
                        Comptes.
                    </h1>
                </div>
                <button
                    type="button"
                    data-testid="cockpit-new-user"
                    onClick={() => setCreating(true)}
                    className="inline-flex items-center gap-3 px-5 py-2.5 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500"
                >
                    <Plus strokeWidth={1.2} size={14} />
                    Nouveau compte
                </button>
            </div>

            <div className="border-t border-white/[0.06]">
                {items.map((u) => (
                    <div
                        key={u.email}
                        data-testid={`user-row-${u.email}`}
                        className="grid lg:grid-cols-[1fr_auto_auto_auto_auto] items-center gap-6 py-6 border-b border-white/[0.06] px-2"
                    >
                        <div className="min-w-0">
                            <div
                                className="font-serif text-[19px] text-[#f4ecd8] leading-tight truncate flex items-center gap-3"
                                style={{ fontWeight: 400 }}
                            >
                                {u.name || u.email}
                                {u.totp_enabled && (
                                    <Shield
                                        strokeWidth={1.2}
                                        size={14}
                                        className="text-[#d4af37]/80"
                                    />
                                )}
                            </div>
                            <div className="mt-1 text-[12px] text-[#f4ecd8]/45 tracking-wide truncate">
                                {u.email}
                            </div>
                        </div>
                        <select
                            value={u.role}
                            onChange={(e) => setRole(u.email, e.target.value)}
                            disabled={u.email === user.email}
                            className="bg-[#0b1220] border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[12px] font-serif text-[#f4ecd8]"
                        >
                            {ROLES.map((r) => (
                                <option key={r.v} value={r.v}>
                                    {r.l}
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={() => setActive(u.email, !u.active)}
                            disabled={u.email === user.email}
                            className={`px-4 py-2 text-[10px] tracking-[0.28em] uppercase border transition-all duration-500 ${
                                u.active
                                    ? "border-[#d4af37]/40 text-[#d4af37]"
                                    : "border-white/15 text-[#f4ecd8]/40"
                            } disabled:opacity-40 disabled:cursor-not-allowed`}
                        >
                            {u.active ? "Actif" : "Désactivé"}
                        </button>
                        <span className="text-[11px] text-[#f4ecd8]/40 tracking-wide hidden lg:block">
                            {u.last_login_at
                                ? new Date(u.last_login_at).toLocaleString("fr-FR")
                                : "Jamais connecté"}
                        </span>
                        <button
                            type="button"
                            onClick={() => remove(u.email)}
                            disabled={u.email === user.email}
                            className="text-[#f4ecd8]/30 hover:text-[#e89090] transition-colors duration-500 disabled:opacity-20 disabled:cursor-not-allowed"
                        >
                            <Trash2 strokeWidth={1.2} size={14} />
                        </button>
                    </div>
                ))}
            </div>

            {creating && (
                <NewUserModal
                    onClose={() => setCreating(false)}
                    onCreated={() => {
                        setCreating(false);
                        load();
                    }}
                />
            )}
        </div>
    );
}

function NewUserModal({ onClose, onCreated }) {
    const [form, setForm] = useState({
        email: "",
        name: "",
        password: "",
        role: "admin",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            await cockpit.post("/users", form);
            onCreated();
        } catch (e) {
            const d = e?.response?.data?.detail;
            if (Array.isArray(d)) {
                // FastAPI/Pydantic validation error (422) → flatten to readable French
                setErr(
                    d
                        .map((it) =>
                            it.loc?.includes("email")
                                ? "Adresse email invalide (TLD réservé ou format incorrect)."
                                : it.msg || "Champ invalide"
                        )
                        .join(" · ")
                );
            } else {
                setErr(d || "Erreur de création");
            }
        } finally {
            setBusy(false);
        }
    };

    const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    return (
        <div
            className="fixed inset-0 z-50 bg-[#070d18]/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6"
            onClick={onClose}
        >
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="bg-[#0b1220] border border-white/10 w-full max-w-[520px] p-10 lg:p-14 mt-12 relative"
            >
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-6 right-6 text-[#f4ecd8]/50 hover:text-[#f4ecd8] transition-colors duration-500"
                >
                    <X strokeWidth={1.2} size={20} />
                </button>
                <div className="overline text-[#d4af37]/80 mb-5">
                    Nouveau collaborateur
                </div>
                <h2
                    className="font-serif text-[26px] text-[#f4ecd8] mb-10"
                    style={{ fontWeight: 300 }}
                >
                    Ouvrir un accès cockpit.
                </h2>
                <div className="space-y-6">
                    <Field
                        label="Nom complet"
                        required
                        value={form.name}
                        onChange={onChange("name")}
                        testid="new-user-name"
                    />
                    <Field
                        label="Email"
                        required
                        type="email"
                        value={form.email}
                        onChange={onChange("email")}
                        testid="new-user-email"
                    />
                    <Field
                        label="Mot de passe initial (≥ 8 caractères)"
                        required
                        type="password"
                        value={form.password}
                        onChange={onChange("password")}
                        testid="new-user-password"
                    />
                    <Select
                        label="Rôle"
                        value={form.role}
                        onChange={onChange("role")}
                        options={ROLES}
                    />
                </div>
                {err && (
                    <div className="mt-6 text-[13px] text-[#e89090] border-l-2 border-[#e89090] pl-4 py-1">
                        {err}
                    </div>
                )}
                <div className="mt-10 flex justify-end gap-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-3 text-[10px] tracking-[0.3em] uppercase text-[#f4ecd8]/60 hover:text-[#f4ecd8] transition-colors duration-500"
                    >
                        Annuler
                    </button>
                    <button
                        type="submit"
                        data-testid="new-user-submit"
                        disabled={busy}
                        className="px-7 py-3 border border-[#d4af37]/40 text-[#d4af37] text-[11px] tracking-[0.3em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                    >
                        {busy ? "Création…" : "Créer le compte"}
                    </button>
                </div>
            </form>
        </div>
    );
}
